/**
 * Compatibility helpers for the react-popper 0.10 → 2.x migration.
 *
 * react-popper 2.x is backed by `@popperjs/core` v2, whose modifier format is an
 * array (`[{name, options}]`) rather than v1's keyed object (`{name: options}`).
 * Dozens of call sites across the app still pass v1-style modifier objects to our
 * atomic components (Dropdown, Menu, Popover, Tooltip, AnchoredFixedPosition), so
 * rather than churn every call site we keep that public shape and translate it to
 * the v2 array here, at the single boundary where react-popper is consumed.
 *
 * @see https://popper.js.org/docs/v2/modifiers/ for the v2 modifier reference.
 */
import type {Modifier, Placement, Rect, State} from '@popperjs/core';

/**
 * The full set of placements, mirrored from `@popperjs/core`'s `placements` enum.
 * We inline it because `@popperjs/core`'s CommonJS entry (used by Jest) does not
 * re-export the runtime `placements` value, only the ESM/types entries do.
 */
export const placements: Placement[] = [
  'top',
  'top-start',
  'top-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'right',
  'right-start',
  'right-end',
  'left',
  'left-start',
  'left-end',
  'auto',
  'auto-start',
  'auto-end'
];

type V2Modifier = Partial<Modifier<string, object>>;

/** A v1 modifier function mutates and returns a `data` object. */
type LegacyModifierFn = (data: any, options?: any) => any;

interface LegacyModifier {
  enabled?: boolean;
  order?: number;
  fn?: LegacyModifierFn;
  [key: string]: unknown;
}

// Loose by design: existing call sites type their modifier props as
// `Record<string, unknown>`, so the object branch accepts any keyed shape and we
// narrow each entry at runtime.
type LegacyModifiers = Record<string, any>;

export type ModifierInput = LegacyModifiers | ReadonlyArray<V2Modifier> | undefined | null;

/** v1 modifier names that map onto a built-in @popperjs/core v2 modifier. */
const STANDARD_MODIFIERS = new Set([
  'offset',
  'preventOverflow',
  'flip',
  'arrow',
  'hide',
  'eventListeners',
  'keepTogether',
  'computeStyles',
  'popperOffsets',
  'applyStyles'
]);

type OffsetTuple = [number, number];
type OffsetFn = (args: {placement: Placement; popper: Rect; reference: Rect}) => OffsetTuple;

interface OffsetPart {
  value: number;
  percentOf?: 'popper' | 'reference';
}

/**
 * Parse one v1 offset component. v1 accepted a bare number, a `px` value, or a
 * percentage: `%p` (of the popper) / `%r` (or bare `%`, of the reference). `parseFloat`
 * strips a trailing unit, so `"8px"` → 8 and `"-100%p"` → -100.
 */
function parseOffsetPart(raw: string): OffsetPart {
  const s = raw.trim();
  const value = parseFloat(s) || 0;
  if (s.endsWith('%p')) {
    return {value, percentOf: 'popper'};
  }
  if (s.endsWith('%r') || s.endsWith('%')) {
    return {value, percentOf: 'reference'};
  }
  return {value};
}

/**
 * v1 `offset` was a `"skidding, distance"` string (px by default, or a `%p`/`%r`
 * percentage). v2 wants a `[skidding, distance]` px tuple, or a function
 * `({placement, popper, reference}) => [skidding, distance]` when the value is a
 * percentage of the popper/reference — which the built-in v2 `offset` modifier
 * also accepts. Distance is measured along the placement (main) axis, skidding
 * along the cross axis, so a percentage resolves against the matching rect dimension.
 */
function parseOffset(offset: unknown): OffsetTuple | OffsetFn | undefined {
  if (Array.isArray(offset)) {
    return [Number(offset[0]) || 0, Number(offset[1]) || 0];
  }
  if (typeof offset === 'number') {
    return [0, offset];
  }
  if (typeof offset !== 'string') {
    return undefined;
  }

  const parts = offset.split(',');
  const skidding = parseOffsetPart(parts.length > 1 ? parts[0] : '0');
  const distance = parseOffsetPart(parts.length > 1 ? parts[1] : parts[0]);

  if (!skidding.percentOf && !distance.percentOf) {
    return [skidding.value, distance.value];
  }

  return ({placement, popper, reference}) => {
    const isVertical = ['top', 'bottom'].includes(placement.split('-')[0]);
    const resolve = (part: OffsetPart, isDistance: boolean): number => {
      if (!part.percentOf) {
        return part.value;
      }
      const rect = part.percentOf === 'popper' ? popper : reference;
      // Distance runs along the main axis; skidding along the cross axis.
      const useHeight = isDistance ? isVertical : !isVertical;
      return (part.value / 100) * (useHeight ? rect.height : rect.width);
    };
    return [resolve(skidding, false), resolve(distance, true)];
  };
}

/**
 * v1's `boundariesElement` maps onto v2's `boundary`/`rootBoundary` options. v1 also
 * accepted a DOM node (its default was 'scrollParent', the reference's scrolling
 * ancestor) — v2's element `boundary` is the equivalent. Note v2's *default* boundary
 * is 'clippingParents' of the popper, which for a fixed-strategy portaled popper
 * resolves to the viewport, NOT the reference's scroll parent; callers that need the
 * old 'scrollParent' clipping must pass that element explicitly.
 */
function mapBoundary(boundariesElement: unknown): {
  boundary?: Element;
  rootBoundary?: 'viewport' | 'document';
} {
  if (boundariesElement === 'viewport') {
    return {rootBoundary: 'viewport'};
  }
  if (boundariesElement === 'window') {
    return {rootBoundary: 'document'};
  }
  if (boundariesElement instanceof Element) {
    return {boundary: boundariesElement};
  }
  return {};
}

function convertStandard(name: string, legacy: LegacyModifier): V2Modifier {
  const {enabled, ...rest} = legacy;
  const modifier: V2Modifier = {name};
  if (enabled !== undefined) {
    modifier.enabled = enabled;
  }

  const options: Record<string, unknown> = {};
  switch (name) {
    case 'offset': {
      const offset = parseOffset(rest.offset);
      if (offset) {
        options.offset = offset;
      }
      break;
    }
    case 'preventOverflow': {
      if (rest.padding !== undefined) {
        options.padding = rest.padding;
      }
      Object.assign(options, mapBoundary(rest.boundariesElement));
      // popper.js v1's preventOverflow guarded every side and shifted the popper as
      // far as needed to keep it in the boundary. v2 only guards the main (placement)
      // axis by default (`altAxis: false`), so enable the alt axis to preserve v1's
      // all-sides keep-in-view behavior.
      options.altAxis = true;
      // v1's `escapeWithReference: true` meant "stay glued to the reference, even if it
      // overflows the boundary" — v2's `tether: true`. Absent (or false), the popper is
      // free to detach from the reference to stay inside the boundary (`tether: false`),
      // which keeps a fixed-strategy dropdown near the viewport edge on-screen.
      options.tether = rest.escapeWithReference === true;
      break;
    }
    case 'flip': {
      if (rest.padding !== undefined) {
        options.padding = rest.padding;
      }
      Object.assign(options, mapBoundary(rest.boundariesElement));
      if (Array.isArray(rest.behavior)) {
        options.fallbackPlacements = rest.behavior;
      }
      break;
    }
    case 'arrow': {
      if (rest.element) {
        options.element = rest.element;
      }
      break;
    }
    case 'eventListeners': {
      if (rest.scroll !== undefined) {
        options.scroll = rest.scroll;
      }
      if (rest.resize !== undefined) {
        options.resize = rest.resize;
      }
      break;
    }
    default:
      break;
  }

  if (Object.keys(options).length > 0) {
    modifier.options = options;
  }
  return modifier;
}

/**
 * Wraps a v1 modifier `fn` (which mutated `data.styles` / `data.offsets`) in a v2
 * modifier. We present the small `data` facade the legacy fns actually read/write
 * — popper styles, popper/reference rects, and popper position — then copy the
 * result back into v2 `state.styles.popper`. Runs after `computeStyles` so it can
 * override the computed transform/position, matching v1's post-`computeStyle` order.
 */
function adaptLegacyFn(name: string, legacy: LegacyModifier): V2Modifier {
  const {enabled, fn} = legacy;
  return {
    name,
    enabled: enabled !== false,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn({state}: {state: State}) {
      // v1's `data.offsets` exposed absolute top/left/right/bottom (viewport-relative
      // for fixed-strategy poppers) alongside width/height. Some legacy fns read those
      // coordinates (e.g. to reposition above the trigger when the viewport is tight).
      // Source them from *this* pass's computed values — `state.rects` (the reference/
      // popper boxes) and `state.modifiersData.popperOffsets` (the popper x/y resolved
      // this cycle) — rather than a live `getBoundingClientRect`, which at `beforeWrite`
      // still reflects the previous pass's position (applyStyles hasn't flushed the new
      // one yet) and so mispositions the popper on first open.
      const {reference: refRect, popper: popRect} = state.rects;
      const popperOffsets = state.modifiersData.popperOffsets ?? {x: 0, y: 0};
      const data = {
        placement: state.placement,
        styles: {...state.styles.popper},
        offsets: {
          popper: {
            top: popperOffsets.y,
            left: popperOffsets.x,
            right: popperOffsets.x + popRect.width,
            bottom: popperOffsets.y + popRect.height,
            width: popRect.width,
            height: popRect.height,
            position: state.styles.popper.position
          },
          reference: {
            top: refRect.y,
            left: refRect.x,
            right: refRect.x + refRect.width,
            bottom: refRect.y + refRect.height,
            width: refRect.width,
            height: refRect.height
          }
        }
      };

      const originalPosition = data.offsets.popper.position;
      const result = fn!(data, legacy) || data;

      if (result.styles) {
        state.styles.popper = result.styles as Partial<CSSStyleDeclaration>;
      }
      // v1 fns signalled a position change via offsets.popper.position (which
      // computeStyle then copied into styles). Replicate that, but only when the
      // fn actually changed it, so we don't clobber a position the fn set directly
      // on its returned styles object.
      const position = result.offsets?.popper?.position;
      if (position && position !== originalPosition) {
        state.styles.popper.position = position;
      }
    }
  };
}

/**
 * Normalize a modifiers prop into the v2 array @popperjs/core expects. Accepts:
 * - a v2 array (passed through untouched),
 * - a v1 keyed object of standard modifiers (mapped to v2 equivalents),
 * - a v1 keyed object containing custom `fn` modifiers (wrapped via {@link adaptLegacyFn}).
 *
 * Non-standard entries without a `fn` were inert no-ops under v1 (e.g. a `setMobile`
 * placeholder with only `{enabled}`), so they are dropped.
 */
export function toModifierArray(input: ModifierInput): V2Modifier[] {
  if (!input) {
    return [];
  }
  if (Array.isArray(input)) {
    return [...input];
  }

  return Object.entries(input as LegacyModifiers).flatMap(([name, legacy]) => {
    if (!legacy || typeof legacy !== 'object') {
      return [];
    }
    if (typeof legacy.fn === 'function') {
      return [adaptLegacyFn(name, legacy)];
    }
    if (STANDARD_MODIFIERS.has(name)) {
      return [convertStandard(name, legacy)];
    }
    return [];
  });
}
